import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/images/header-logo.png";
import whiteLogo from "../assets/images/header-white-logo.png";

import {
    Menu,
    X,
    Settings,
    LogOut,
    LogIn,
    Moon,
    Sun,
} from "lucide-react";


export default function Header({ user, setUser, onLoginClick }) {

    const [darkMode, setDarkMode] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [userOpen, setUserOpen] = useState(false);
    const desktopUserRef = useRef(null);
    const mobileUserRef = useRef(null);


    const location = useLocation();

    const isActive = (path) =>
        location.pathname === path;


    useEffect(() => {

        if (darkMode) {
            document.documentElement.classList.add("dark");
        }
        else {
            document.documentElement.classList.remove("dark");
        }

    }, [darkMode]);

    useEffect(() => {

        const handleClickOutside = (event) => {

            if (
                desktopUserRef.current &&
                !desktopUserRef.current.contains(event.target) &&
                mobileUserRef.current &&
                !mobileUserRef.current.contains(event.target)
            ) {
                setUserOpen(false);
            }

        };


        document.addEventListener(
            "mousedown",
            handleClickOutside
        );


        return () => {
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
        };


    }, []);


    const logout = () => {
        localStorage.removeItem("user");
        setUser(null);
    };


    const closeMobileMenu = () =>
        setMenuOpen(false);



    const linkStyle = (path) => {

        return `
        uppercase 
        tracking-[0.18em] 
        text-sm 
        transition 
        duration-300

        ${isActive(path)
                ?
                "text-[#D9BB74] border-b border-[#D9BB74] pb-1"
                :
                "text-white dark:text-black hover:text-[#D9BB74]"
            }
        `;
    };



    const mobileNavClass = (path) =>
        `
    block
    py-4
    uppercase
    tracking-[0.18em]
    text-sm
    border-b
    border-white/10

    ${isActive(path)
            ?
            "text-[#D9BB74]"
            :
            "text-white hover:text-[#D9BB74]"
        }
    `;



    return (

        <header className="
sticky 
top-0 
z-50
bg-[#10161F]
dark:bg-white
border-b
border-[#D9BB7420]
py-3
">


            <div className="container mx-auto px-6">


                {/* DESKTOP HEADER */}

                <div className="
hidden
lg:grid
grid-cols-[1fr_auto_1fr_auto]
items-center
h-24
gap-8
">



                    {/* LEFT MENU */}

                    <nav className="
flex
justify-end
items-center
gap-8
">


                        <Link
                            to="/"
                            className={linkStyle("/")}
                        >
                            Home
                        </Link>


                        <Link
                            to="/hotels"
                            className={linkStyle("/hotels")}
                        >
                            Hotels
                        </Link>


                        <Link
                            to="/about-us"
                            className={linkStyle("/about-us")}
                        >
                            About
                        </Link>


                    </nav>





                    {/* CENTER LOGO */}


                    <div className="flex justify-center px-16">
                        <Link to="/">
                            <img
                                src={darkMode ? logo : whiteLogo}
                                alt="Dubai Hotels"
                                className="h-20 w-auto object-contain"
                            />
                        </Link>
                    </div>





                    {/* RIGHT MENU */}


                    <nav className="
flex
justify-start
items-center
gap-8
">


                        <Link
                            to="/services"
                            className={linkStyle("/services")}
                        >
                            Services
                        </Link>


                        <Link
                            to="/contact-us"
                            className={linkStyle("/contact-us")}
                        >
                            Contact
                        </Link>


                        <Link
                            to="/blogs"
                            className={linkStyle("/blogs")}
                        >
                            Blog
                        </Link>


                    </nav>





                    {/* ACTION AREA */}


                    <div className="
flex
items-center
gap-5
">


                        <button

                            onClick={() => setDarkMode(!darkMode)}

                            className="
text-white
dark:text-black
hover:text-[#D9BB74]
transition
"

                        >

                            {
                                darkMode
                                    ?
                                    <Sun size={19} />
                                    :
                                    <Moon size={19} />
                            }

                        </button>



                        {
                            !user ?

                                <button

                                    onClick={onLoginClick}

                                    className="
    border
    border-[#D9BB7480]
    px-6
    py-3
    uppercase
    tracking-[0.15em]
    text-sm

    text-white
    dark:text-black

    hover:border-[#D9BB74]
    hover:text-[#D9BB74]

    dark:hover:text-[#D9BB74]

    transition
    "

                                >

                                    Sign In

                                </button>


                                :

                                <div
                                    ref={desktopUserRef}
                                    className="relative"
                                >


                                    <button

                                        onClick={() => setUserOpen(!userOpen)}

                                        className="
flex
items-center
gap-3
"

                                    >


                                        <div className="
w-10
h-10
rounded-full
bg-[#D9BB74]
text-black
flex
items-center
justify-center
font-semibold
">

                                            {user?.name?.charAt(0)}

                                        </div>



                                        <span className="
text-white
dark:text-black
">

                                            {user?.name?.split(" ")[0]}

                                        </span>


                                    </button>



                                    {
                                        userOpen &&

                                        <div className="
absolute
right-0
mt-4
w-56
bg-white
rounded-xl
shadow-xl
overflow-hidden
">


                                            <Link

                                                to="/profile"

                                                className="
flex
items-center
gap-3
px-5
py-3
hover:bg-gray-100
"

                                            >

                                                <Settings size={16} />

                                                Profile

                                            </Link>



                                            <button

                                                onClick={() => {
                                                    logout();
                                                    setUserOpen(false);
                                                }}

                                                className="
flex
items-center
gap-3
px-5
py-3
text-red-600
hover:bg-red-50
w-full
"

                                            >

                                                <LogOut size={16} />

                                                Logout

                                            </button>



                                        </div>

                                    }



                                </div>

                        }



                    </div>


                </div>


                {/* MOBILE HEADER */}

                <div className="
lg:hidden
flex
items-center
justify-between
">


                    {/* MOBILE LOGO */}

                    <Link to="/">
                        <img src={darkMode ? logo : whiteLogo} alt="Dubai Hotels" className=" h-20 w-auto object-contain pr-5" />
                    </Link>




                    {/* MOBILE ACTIONS */}

                    <div className="
flex
items-center
gap-4
">


                        <button

                            onClick={() => setDarkMode(!darkMode)}

                            className="
text-white
dark:text-black
hover:text-[#D9BB74]
"

                        >

                            {
                                darkMode
                                    ?
                                    <Sun size={20} />
                                    :
                                    <Moon size={20} />
                            }

                        </button>




                        {
                            !user ?

                                <button

                                    onClick={onLoginClick}

                                    className="
text-[#D9BB74]
"

                                >

                                    <LogIn size={22} />

                                </button>


                                :

                                <div
                                    ref={mobileUserRef}
                                    className="relative"
                                >


                                    <button

                                        onClick={() => setUserOpen(!userOpen)}

                                        className="
flex
items-center
"

                                    >


                                        <div className="
w-9
h-9
rounded-full
bg-[#D9BB74]
text-black
flex
items-center
justify-center
font-semibold
">

                                            {user?.name?.charAt(0)}

                                        </div>


                                    </button>



                                    {
                                        userOpen &&

                                        <div className="
absolute
right-0
top-12
w-52
bg-white
dark:bg-gray-800
rounded-xl
shadow-xl
py-2
z-50
">


                                            <Link

                                                to="/profile"

                                                className="
flex
items-center
gap-3
px-4
py-3
hover:bg-gray-100
dark:hover:bg-gray-700
"

                                            >

                                                <Settings size={16} />

                                                Profile

                                            </Link>



                                            <button

                                                onClick={() => {
                                                    logout();
                                                    setUserOpen(false);
                                                }}

                                                className="
flex
items-center
gap-3
px-4
py-3
text-red-500
hover:bg-red-50
w-full
"

                                            >

                                                <LogOut size={16} />

                                                Logout

                                            </button>


                                        </div>

                                    }



                                </div>

                        }





                        <button

                            onClick={() => setMenuOpen(true)}

                            className="
text-white
dark:text-black
"

                        >

                            <Menu size={28} />

                        </button>



                    </div>


                </div>





                {/* MOBILE OVERLAY */}


                <div

                    className={`
fixed
inset-0
bg-black/50
z-40
transition-opacity
lg:hidden

${menuOpen
                            ?
                            "opacity-100"
                            :
                            "opacity-0 pointer-events-none"
                        }

`}

                    onClick={() => setMenuOpen(false)}

                />






                {/* MOBILE MENU */}


                <div

                    className={`
fixed
top-0
left-0
w-full
bg-[#10161F]
dark:bg-white
z-50
shadow-2xl
rounded-b-3xl
transition-transform
duration-300
lg:hidden

${menuOpen
                            ?
                            "translate-x-0"
                            :
                            "-translate-x-full"
                        }

`}

                >



                    {/* MOBILE MENU HEADER */}


                    <div className="
flex
items-center
justify-between
p-5
border-b
border-white/10
">


                        <Link to="/">
                            <img src={darkMode ? logo : whiteLogo} alt="Dubai Hotels" className="h-20 w-auto pr-5" />
                        </Link>




                        <button

                            onClick={() => setMenuOpen(false)}

                            className="
text-white
dark:text-black
"

                        >

                            <X size={26} />

                        </button>



                    </div>






                    {/* MOBILE LINKS */}


                    <nav className="
flex
flex-col
px-6
py-5
">


                        <Link

                            to="/"

                            onClick={closeMobileMenu}

                            className={mobileNavClass("/")}

                        >

                            Home

                        </Link>



                        <Link

                            to="/hotels"

                            onClick={closeMobileMenu}

                            className={mobileNavClass("/hotels")}

                        >

                            Hotels

                        </Link>



                        <Link

                            to="/about-us"

                            onClick={closeMobileMenu}

                            className={mobileNavClass("/about-us")}

                        >

                            About

                        </Link>



                        <Link

                            to="/services"

                            onClick={closeMobileMenu}

                            className={mobileNavClass("/services")}

                        >

                            Services

                        </Link>



                        <Link

                            to="/contact-us"

                            onClick={closeMobileMenu}

                            className={mobileNavClass("/contact-us")}

                        >

                            Contact

                        </Link>



                        <Link

                            to="/blogs"

                            onClick={closeMobileMenu}

                            className={mobileNavClass("/blogs")}

                        >

                            Blog

                        </Link>



                    </nav>



                </div>



            </div>

        </header>

    );

}